import { Controller } from "@hotwired/stimulus";
import { DirectUpload } from "@rails/activestorage";

// MVT Slice 3 (T-S3-WS4-A02) — dedicated trim page UI component.
//
// Lets a user pick a source video, scrub it, set in/out points on a timeline
// (touch + keyboard accessible via native range inputs), preview the selection,
// reset, cancel, and confirm. Confirm uploads the source and asks the server to
// clip it — but the apply/status endpoints + background job are A03, so when
// `applyUrlValue` is blank the confirm step stops at a "ready" placeholder
// instead of hitting a non-existent endpoint.
const MIN_SEGMENT_SECONDS = 0.5;
// Must match `.video-trim__range` thumb width in _video_trim.scss. Native range
// thumbs are inset by half their width at each end, so the selection fill and
// playhead are positioned in pixels with the same inset to line their edges up
// with the *center* of each handle.
const THUMB_WIDTH = 22;
// How long the centered play/pause control stays visible during playback before
// auto-hiding so it does not block the video.
const PLAYBACK_HIDE_MS = 2000;

export default class extends Controller {
  static targets = [
    "fileInput",
    "editor",
    "emptyState",
    "previewFrame",
    "preview",
    "sourceMeta",
    "fileInfo",
    "fileName",
    "timeline",
    "selection",
    "maskBefore",
    "maskAfter",
    "playheadIndicator",
    "startRange",
    "endRange",
    "durationLabel",
    "selectionLabel",
    "playheadLabel",
    "playbackButton",
    "playbackIcon",
    "playPauseButton",
    "playPauseIcon",
    "stopButton",
    "confirmButton",
    "progress",
    "progressBar",
    "progressPct",
    "status",
  ];

  static values = {
    applyUrl: String,
    statusUrl: String,
    directUploadUrl: String,
    target: String,
    recordId: String,
    cancelUrl: String,
    // Slice 6 WS-4 (re-trim mode): when sourceUrl is present the page skips the
    // file picker, loads the retained original for scrubbing, prefills the
    // previous range, and confirms via reapplyUrl (no DirectUpload).
    sourceUrl: String,
    reapplyUrl: String,
    prefillStart: Number,
    prefillEnd: Number,
    prefillFps: Number,
  };

  connect() {
    this.duration = 0;
    this.sourceFile = null;
    this.sourceWidth = 0;
    this.sourceHeight = 0;
    this.segmentPreviewActive = false;
    this.busy = false;
    this.retrimMode = false;
    // Canonical playhead position, decoupled from the preview's currentTime so a
    // handle drag can move the previewed frame without moving the pin.
    this.playheadTime = 0;
    this.handleDragging = false;
    this.confirmDefaultLabel = this.hasConfirmButtonTarget
      ? this.confirmButtonTarget.textContent.trim()
      : "Upload video";
    this.renderEmptyState();
    this.updateControls();
    // Re-trim mode: a retained original is available, so load it straight into
    // the editor instead of asking for a file. First-time trims (no sourceUrl)
    // keep the upload/apply path unchanged.
    if (this.hasSourceUrlValue && this.sourceUrlValue) this.enterRetrimMode();
  }

  // True once a source is loaded for editing, whether it is a freshly picked
  // local File (first-time trim) or the retained original (re-trim mode).
  hasSourceLoaded() {
    return !!this.sourceFile || this.retrimMode;
  }

  enterRetrimMode() {
    this.retrimMode = true;
    this.segmentPreviewActive = false;
    this.previewTarget.src = this.sourceUrlValue;
    this.emptyStateTarget.hidden = true;
    this.previewFrameTarget.hidden = false;
    this.fileInfoTarget.hidden = true;
    this.setStatus("Loading your video…");
    this.updateControls();
  }

  openFilePicker() {
    // In re-trim mode the source is fixed (the retained original); there is no
    // file to pick, and re-uploading is not part of this flow.
    if (this.busy || this.retrimMode) return;
    this.fileInputTarget.click();
  }

  fileChanged() {
    const file = this.fileInputTarget.files[0];
    if (!file) return;

    this.sourceFile = file;
    this.segmentPreviewActive = false;
    if (this.objectUrl) URL.revokeObjectURL(this.objectUrl);
    this.objectUrl = URL.createObjectURL(file);
    this.previewTarget.src = this.objectUrl;
    this.emptyStateTarget.hidden = true;
    this.previewFrameTarget.hidden = false;
    this.fileInfoTarget.hidden = false;
    this.setStatus("Loading video…");
    this.updateControls();
  }

  onMetadata() {
    const preview = this.previewTarget;
    this.duration = preview.duration || 0;
    this.sourceWidth = preview.videoWidth || 0;
    this.sourceHeight = preview.videoHeight || 0;

    [this.startRangeTarget, this.endRangeTarget].forEach((range) => {
      range.min = "0";
      range.max = String(this.duration);
    });

    // Re-trim mode prefills the previous selection; a first-time trim opens on
    // the full clip. Prefill values are clamped to the duration by setRange.
    if (this.retrimMode && this.hasPrefillEndValue && this.prefillEndValue > 0) {
      this.setRange(this.prefillStartValue || 0, this.prefillEndValue);
    } else {
      this.setRange(0, this.duration);
    }
    const start = this.readRange().start;
    this.previewTarget.currentTime = start;
    this.setPin(start);

    if (this.sourceFile) {
      this.fileNameTarget.textContent = this.sourceFile.name;
      this.sourceMetaTarget.textContent = `${this.formatBytes(this.sourceFile.size)} · ${this.duration.toFixed(
        1
      )}s · ${this.sourceWidth}×${this.sourceHeight}`;
    }
    this.setStatus("");
  }

  // While a trim handle is dragged the preview frame follows that handle so the
  // user sees the moment they are cutting to, but the playhead pin stays where it
  // was — unless the handle crosses it, in which case the pin follows the handle.
  beginHandleDrag() {
    if (this.handleDragging) return;
    this.handleDragging = true;
    this.segmentPreviewActive = false;
    this.pausePreview();
  }

  startHandleInput() {
    this.beginHandleDrag();
    let start = parseFloat(this.startRangeTarget.value) || 0;
    const end = parseFloat(this.endRangeTarget.value) || 0;
    if (start > end - MIN_SEGMENT_SECONDS) {
      start = Math.max(0, end - MIN_SEGMENT_SECONDS);
    }
    this.setRange(start, end);
    // Frame follows the moving start handle.
    this.previewTarget.currentTime = this.readRange().start;
    // Pin only moves if the start handle passed it.
    this.setPin(this.clampToSelection(this.playheadTime));
  }

  endHandleInput() {
    this.beginHandleDrag();
    const start = parseFloat(this.startRangeTarget.value) || 0;
    let end = parseFloat(this.endRangeTarget.value) || 0;
    if (end < start + MIN_SEGMENT_SECONDS) {
      end = Math.min(this.duration, start + MIN_SEGMENT_SECONDS);
    }
    this.setRange(start, end);
    // Frame follows the moving end handle.
    this.previewTarget.currentTime = this.readRange().end;
    // Pin only moves if the end handle passed it.
    this.setPin(this.clampToSelection(this.playheadTime));
  }

  // Drag released: the previewed frame returns to the playhead pin.
  handleDragEnd() {
    if (!this.handleDragging) return;
    this.handleDragging = false;
    this.setPin(this.clampToSelection(this.playheadTime));
    this.previewTarget.currentTime = this.playheadTime;
  }

  togglePlayback() {
    if (!this.hasSourceLoaded() || this.busy) return;
    if (!this.previewTarget.paused) {
      this.pausePreview();
      return;
    }
    this.playSelection();
  }

  playSegment() {
    this.playSelection();
  }

  playSelection() {
    const { start, end } = this.readRange();
    if (!(end > start)) return;
    this.segmentPreviewActive = true;
    const current = this.previewTarget.currentTime || 0;
    if (current < start || current >= end - 0.05) {
      this.previewTarget.currentTime = start;
      this.setPin(start);
    }
    this.previewTarget.play().catch(() => {
      this.segmentPreviewActive = false;
      this.onPlaybackStateChanged();
    });
  }

  onTimeUpdate() {
    // During a handle drag the preview seeks to follow the handle; keep the pin
    // where it is instead of letting these transient seeks move it.
    if (this.handleDragging) return;
    const currentTime = this.previewTarget.currentTime || 0;
    this.setPin(currentTime);
    if (!this.segmentPreviewActive) return;
    const { end } = this.readRange();
    if (currentTime >= end - 0.05) {
      this.pausePreview();
      this.previewTarget.currentTime = end;
      this.setPin(end);
      this.segmentPreviewActive = false;
    }
  }

  onPlaybackStateChanged() {
    const isPlaying = !this.previewTarget.paused;
    if (this.hasPlaybackButtonTarget) {
      this.playbackButtonTarget.setAttribute("aria-label", isPlaying ? "Pause selected clip" : "Play selected clip");
      this.playbackButtonTarget.classList.toggle("video-trim__playback--playing", isPlaying);
      // Paused → keep the control visible so the user can resume; playing →
      // reveal then auto-hide so it stops obstructing the video.
      if (isPlaying) {
        this.revealPlayback();
      } else {
        this.showPlayback();
      }
    }
    // Keep the timeline Play/Pause control in sync with the on-video overlay.
    if (this.hasPlayPauseButtonTarget) {
      this.playPauseButtonTarget.setAttribute("aria-label", isPlaying ? "Pause" : "Play");
      this.playPauseButtonTarget.classList.toggle("video-trim__control--playing", isPlaying);
    }
  }

  // Reveal the control and, while playing, schedule it to fade out again.
  revealPlayback() {
    if (!this.hasPlaybackButtonTarget || !this.hasSourceLoaded()) return;
    this.showPlayback();
    if (!this.previewTarget.paused) {
      this.hideTimer = setTimeout(() => {
        if (!this.previewTarget.paused) {
          this.playbackButtonTarget.classList.add("video-trim__playback--hidden");
        }
      }, PLAYBACK_HIDE_MS);
    }
  }

  showPlayback() {
    if (!this.hasPlaybackButtonTarget) return;
    clearTimeout(this.hideTimer);
    this.playbackButtonTarget.classList.remove("video-trim__playback--hidden");
  }

  // Stop playback and return the playhead to the start of the selection. Does
  // not change the trim range.
  stop() {
    if (!this.hasSourceLoaded()) return;
    this.pausePreview();
    this.segmentPreviewActive = false;
    const { start } = this.readRange();
    this.previewTarget.currentTime = start;
    this.setPin(start);
  }

  clearVideo() {
    if (this.busy) return;
    this.pausePreview();
    this.segmentPreviewActive = false;
    this.duration = 0;
    this.sourceFile = null;
    this.sourceWidth = 0;
    this.sourceHeight = 0;
    this.fileInputTarget.value = "";
    this.previewTarget.removeAttribute("src");
    this.previewTarget.load();
    if (this.objectUrl) URL.revokeObjectURL(this.objectUrl);
    this.objectUrl = null;
    this.renderEmptyState();
    this.setRange(0, 0);
    this.handleDragging = false;
    this.playheadTime = 0;
    this.renderPlayhead(0);
    this.setStatus("");
  }

  cancel() {
    if (this.hasCancelUrlValue && this.cancelUrlValue) {
      window.location.assign(this.cancelUrlValue);
    } else {
      window.history.back();
    }
  }

  async confirm() {
    if (this.busy) return;
    const { start, end } = this.readRange();
    if (!this.hasSourceLoaded() || !(end - start >= MIN_SEGMENT_SECONDS)) {
      this.setStatus("Choose a clip of at least half a second first.");
      return;
    }

    // Re-trim mode: no upload — reprocess the retained original with the new range.
    if (this.retrimMode) {
      await this.confirmRetrim(start, end);
      return;
    }

    if (!this.applyUrlValue) {
      this.setStatus(
        `Ready: ${this.formatTime(start)}–${this.formatTime(end)} (${this.formatTime(end - start)}).`
      );
      return;
    }

    this.setBusy(true);
    this.setConfirmLabel("Uploading…");
    this.showProgress();
    this.setProgress(0);
    try {
      this.setStatus("Uploading your video…");
      const signedId = await this.directUpload(this.sourceFile, (progress) => {
        this.setProgress(progress);
        this.setStatus(`Uploading your video… ${Math.round(progress * 100)}%`);
      });

      // Upload done; the server-side clip submission has no byte progress, so
      // switch the bar to an indeterminate "working" state.
      this.setProgress(1);
      this.setConfirmLabel("Processing…");
      this.showProgress({ indeterminate: true });
      this.setStatus("Trimming on the server…");
      await this.submitTrim(signedId, start, end);

      // Finalization runs in the background (Videos::AdvanceTrimJob + sweep);
      // send the user back to the page that shows the processing placeholder.
      this.setStatus("Your trimmed video is processing…");
      if (this.hasCancelUrlValue && this.cancelUrlValue) {
        window.location.assign(this.cancelUrlValue);
      } else {
        this.setStatus("Your trimmed video is processing. You can head back now.");
        this.hideProgress();
        this.setConfirmLabel(this.confirmDefaultLabel);
        this.setBusy(false);
      }
    } catch (error) {
      this.setStatus(`Something went wrong: ${error.message}`);
      this.hideProgress();
      this.setConfirmLabel(this.confirmDefaultLabel);
      this.setBusy(false);
    }
  }

  // Re-trim confirm: reprocess the retained original with the new range. No
  // DirectUpload — just POST the range to the reapply endpoint, then return to
  // the source page (which shows the processing placeholder while it finalizes).
  async confirmRetrim(start, end) {
    if (!this.reapplyUrlValue) {
      this.setStatus("Re-trim is not available right now.");
      return;
    }
    this.setBusy(true);
    this.setConfirmLabel("Processing…");
    this.showProgress({ indeterminate: true });
    this.setStatus("Re-trimming on the server…");
    try {
      await this.submitReapply(start, end);
      this.setStatus("Your trimmed video is processing…");
      if (this.hasCancelUrlValue && this.cancelUrlValue) {
        window.location.assign(this.cancelUrlValue);
      } else {
        this.setStatus("Your trimmed video is processing. You can head back now.");
        this.hideProgress();
        this.setConfirmLabel(this.confirmDefaultLabel);
        this.setBusy(false);
      }
    } catch (error) {
      this.setStatus(`Something went wrong: ${error.message}`);
      this.hideProgress();
      this.setConfirmLabel(this.confirmDefaultLabel);
      this.setBusy(false);
    }
  }

  // --- helpers ---

  readRange() {
    return {
      start: parseFloat(this.startRangeTarget.value) || 0,
      end: parseFloat(this.endRangeTarget.value) || 0,
    };
  }

  setRange(start, end) {
    let s = Math.max(0, Math.min(start, this.duration));
    let e = Math.max(0, Math.min(end, this.duration));
    if (e < s + MIN_SEGMENT_SECONDS) {
      e = Math.min(this.duration, s + MIN_SEGMENT_SECONDS);
      if (e <= s) s = Math.max(0, e - MIN_SEGMENT_SECONDS);
    }

    this.startRangeTarget.value = String(s);
    this.endRangeTarget.value = String(e);

    this.renderSelection(s, e);
    this.updateControls();
  }

  renderSelection(start, end) {
    const leftPx = this.pxForTime(start);
    const rightPx = this.pxForTime(end);
    this.selectionTarget.style.left = `${leftPx}px`;
    this.selectionTarget.style.width = `${Math.max(0, rightPx - leftPx)}px`;
    // Gray, non-playable regions before the start and after the end handle.
    if (this.hasMaskBeforeTarget) this.maskBeforeTarget.style.width = `${leftPx}px`;
    if (this.hasMaskAfterTarget) this.maskAfterTarget.style.left = `${rightPx}px`;
    this.durationLabelTarget.textContent = `Duration ${this.formatTime(this.duration)}`;
    this.selectionLabelTarget.textContent = `${this.formatTime(end - start)} selected`;
  }

  renderPlayhead(seconds) {
    if (!this.hasPlayheadIndicatorTarget) return;
    this.playheadIndicatorTarget.style.left = `${this.pxForTime(seconds)}px`;
  }

  // The playhead pin position, kept separate from the preview's currentTime.
  setPin(seconds) {
    this.playheadTime = seconds;
    this.renderPlayhead(seconds);
    if (this.hasPlayheadLabelTarget) this.playheadLabelTarget.textContent = this.formatTime(seconds);
  }

  clampToSelection(seconds) {
    const { start, end } = this.readRange();
    return Math.min(Math.max(seconds, start), end);
  }

  // --- timeline geometry (thumb-inset aware so visuals align to handle centers) ---

  usableTrackWidth() {
    const width = this.timelineTarget.getBoundingClientRect().width;
    return Math.max(1, width - THUMB_WIDTH);
  }

  pxForTime(seconds) {
    const ratio = this.duration ? Math.max(0, Math.min(1, seconds / this.duration)) : 0;
    return THUMB_WIDTH / 2 + ratio * this.usableTrackWidth();
  }

  timeForClientX(clientX) {
    const rect = this.timelineTarget.getBoundingClientRect();
    const x = clientX - rect.left - THUMB_WIDTH / 2;
    const ratio = Math.max(0, Math.min(1, x / this.usableTrackWidth()));
    return ratio * this.duration;
  }

  // Drag the playhead (or click anywhere on the open track) to seek the preview.
  beginScrub(event) {
    if (!this.hasSourceLoaded() || this.busy) return;
    // A pointerdown on a trim handle bubbles up from the range input to the
    // timeline; ignore it so grabbing a handle never also scrubs the playhead.
    if (event.target === this.startRangeTarget || event.target === this.endRangeTarget) return;
    event.preventDefault();
    this.segmentPreviewActive = false;
    this.scrubbing = true;
    this.scrubTo(event);
    this.boundScrubMove = this.boundScrubMove || ((e) => this.scrubTo(e));
    this.boundScrubEnd = this.boundScrubEnd || (() => this.endScrub());
    document.addEventListener("pointermove", this.boundScrubMove);
    document.addEventListener("pointerup", this.boundScrubEnd);
  }

  scrubTo(event) {
    if (!this.scrubbing) return;
    // Clamp to the selection so the playhead can never enter the gray zones.
    const time = this.clampToSelection(this.timeForClientX(event.clientX));
    this.previewTarget.currentTime = time;
    this.setPin(time);
  }

  endScrub() {
    this.scrubbing = false;
    document.removeEventListener("pointermove", this.boundScrubMove);
    document.removeEventListener("pointerup", this.boundScrubEnd);
  }

  pausePreview() {
    if (!this.previewTarget.paused) this.previewTarget.pause();
    this.onPlaybackStateChanged();
  }

  renderEmptyState() {
    if (this.hasEmptyStateTarget) this.emptyStateTarget.hidden = false;
    if (this.hasPreviewFrameTarget) this.previewFrameTarget.hidden = true;
    if (this.hasFileInfoTarget) this.fileInfoTarget.hidden = true;
    if (this.hasFileNameTarget) this.fileNameTarget.textContent = "No video selected";
    if (this.hasSourceMetaTarget) this.sourceMetaTarget.textContent = "";
    this.durationLabelTarget.textContent = "Duration 0:00";
    this.playheadLabelTarget.textContent = "0:00";
    this.selectionLabelTarget.textContent = "0:00 selected";
  }

  updateControls() {
    const { start, end } = this.readRange();
    const valid = this.hasSourceLoaded() && end - start >= MIN_SEGMENT_SECONDS;
    const enabled = valid && !this.busy;
    if (this.hasConfirmButtonTarget) this.confirmButtonTarget.disabled = !enabled;
    if (this.hasPlaybackButtonTarget) this.playbackButtonTarget.disabled = !enabled;
    if (this.hasPlayPauseButtonTarget) this.playPauseButtonTarget.disabled = !enabled;
    if (this.hasStopButtonTarget) this.stopButtonTarget.disabled = !this.hasSourceLoaded() || this.busy;
  }

  setBusy(isBusy) {
    this.busy = isBusy;
    this.updateControls();
  }

  setStatus(message) {
    if (this.hasStatusTarget) this.statusTarget.textContent = message;
  }

  setConfirmLabel(text) {
    if (this.hasConfirmButtonTarget) this.confirmButtonTarget.textContent = text;
  }

  showProgress({ indeterminate = false } = {}) {
    if (!this.hasProgressTarget) return;
    this.progressTarget.hidden = false;
    this.progressTarget.classList.toggle("video-trim__progress--indeterminate", indeterminate);
  }

  setProgress(fraction) {
    const pct = Math.max(0, Math.min(100, Math.round((fraction || 0) * 100)));
    if (this.hasProgressTarget) {
      this.progressTarget.classList.remove("video-trim__progress--indeterminate");
      this.progressTarget.setAttribute("aria-valuenow", String(pct));
    }
    if (this.hasProgressBarTarget) this.progressBarTarget.style.width = `${pct}%`;
    if (this.hasProgressPctTarget) this.progressPctTarget.textContent = `${pct}%`;
  }

  hideProgress() {
    if (!this.hasProgressTarget) return;
    this.progressTarget.hidden = true;
    this.progressTarget.classList.remove("video-trim__progress--indeterminate");
    if (this.hasProgressBarTarget) this.progressBarTarget.style.width = "0%";
  }

  directUpload(file, onProgress) {
    return new Promise((resolve, reject) => {
      const upload = new DirectUpload(file, this.directUploadUrlValue, {
        directUploadWillStoreFileWithXHR: (xhr) => {
          xhr.upload.addEventListener("progress", (event) => {
            if (event.lengthComputable && onProgress) {
              onProgress(event.loaded / event.total);
            }
          });
        },
      });
      upload.create((error, blob) => {
        if (error) reject(error);
        else resolve(blob.signed_id);
      });
    });
  }

  submitTrim(signedId, start, end) {
    const body = new FormData();
    body.append("signed_id", signedId);
    body.append("start_sec", String(start));
    body.append("end_sec", String(end));
    body.append("source_width", String(this.sourceWidth || 0));
    body.append("source_height", String(this.sourceHeight || 0));
    // Adoption metrics: the full source length so the server can tell a kept-full
    // upload from an actual trim (a tail-only cut is invisible without it).
    body.append("duration_sec", String(this.duration || 0));

    return fetch(this.applyUrlValue, {
      method: "POST",
      credentials: "same-origin",
      headers: {
        Accept: "application/json",
        "X-CSRF-Token": this.csrfToken(),
      },
      body,
    }).then(async (response) => {
      const payload = await response.json().catch(() => ({}));
      if (response.ok && payload.ok) return payload;
      const detail = payload.error || response.statusText || `HTTP ${response.status}`;
      throw new Error(detail);
    });
  }

  submitReapply(start, end) {
    const body = new FormData();
    body.append("start_sec", String(start));
    body.append("end_sec", String(end));
    body.append("source_width", String(this.sourceWidth || 0));
    body.append("source_height", String(this.sourceHeight || 0));
    body.append("duration_sec", String(this.duration || 0));

    return fetch(this.reapplyUrlValue, {
      method: "POST",
      credentials: "same-origin",
      headers: {
        Accept: "application/json",
        "X-CSRF-Token": this.csrfToken(),
      },
      body,
    }).then(async (response) => {
      const payload = await response.json().catch(() => ({}));
      if (response.ok && payload.ok) return payload;
      const detail = payload.error || response.statusText || `HTTP ${response.status}`;
      throw new Error(detail);
    });
  }

  csrfToken() {
    const meta = document.querySelector("meta[name='csrf-token']");
    return meta ? meta.content : "";
  }

  formatTime(seconds) {
    const s = Math.max(0, seconds || 0);
    const mins = Math.floor(s / 60);
    const secs = Math.floor(s % 60);
    return `${mins}:${String(secs).padStart(2, "0")}`;
  }

  formatBytes(bytes) {
    if (!bytes) return "0 B";
    const units = ["B", "KB", "MB", "GB"];
    let size = bytes;
    let i = 0;
    while (size >= 1024 && i < units.length - 1) {
      size /= 1024;
      i += 1;
    }
    return `${size.toFixed(1)} ${units[i]}`;
  }

  disconnect() {
    clearTimeout(this.hideTimer);
    if (this.boundScrubMove) document.removeEventListener("pointermove", this.boundScrubMove);
    if (this.boundScrubEnd) document.removeEventListener("pointerup", this.boundScrubEnd);
    if (this.objectUrl) URL.revokeObjectURL(this.objectUrl);
  }
}
